/**
 * Fix Student Status
 * Find all "graduated" students and make them active
 *
 * USAGE: await window.fixStudentStatus()
 */

import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase.js';

async function fixStudentStatus() {
  console.log('🔧 Finding all graduated students to reactivate...\n');

  try {
    // Get schoolId
    let schoolId = null;
    const keys = Object.keys(localStorage);
    const contextKey = keys.find(k => k.startsWith('swan_school_context_'));

    if (contextKey) {
      const contextData = localStorage.getItem(contextKey);
      if (contextData) {
        schoolId = JSON.parse(contextData).schoolId;
      }
    }

    if (!schoolId) {
      console.error('No schoolId found');
      return;
    }

    // Get all students
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const snapshot = await getDocs(studentsRef);

    // Find ALL graduated students (except the sample ones)
    const toReactivate = [];
    snapshot.forEach(doc => {
      const student = { id: doc.id, ...doc.data() };
      // Skip sample/test students
      if (student.firstName && (
          student.firstName.includes('Stark') ||
          student.firstName.includes('Parker') ||
          student.firstName.includes('Swift') ||
          student.firstName.includes('Gaga') ||
          student.firstName.includes('Sample')
      )) {
        return; // Skip sample data
      }

      // If they're graduated but have a classId, they're probably current students
      if (student.status === 'graduated' && student.classId) {
        toReactivate.push(student);
      }
    });

    console.log(`Found ${toReactivate.length} students marked as graduated with classIds`);

    if (toReactivate.length === 0) {
      console.log('No students to reactivate');
      return { success: true, reactivated: 0 };
    }

    // Group by className for display
    const byClass = {};
    toReactivate.forEach(s => {
      const className = s.className || 'Unknown';
      if (!byClass[className]) byClass[className] = [];
      byClass[className].push(s);
    });

    console.log('\nStudents to reactivate (by class):');
    Object.entries(byClass).sort().forEach(([className, students]) => {
      console.log(`\n${className} (${students.length} students):`);
      students.slice(0, 5).forEach(s => {
        console.log(`  - ${s.firstName} ${s.surname}`);
      });
      if (students.length > 5) {
        console.log(`  ... and ${students.length - 5} more`);
      }
    });

    console.log(`\n⚠️  This will change ${toReactivate.length} students from "graduated" to "active"`);
    console.log('To proceed, run: await window.fixStudentStatusConfirm()');

    // Store for confirmation
    window._studentsToReactivate = { schoolId, students: toReactivate };

    return { success: true, found: toReactivate.length, pending: true };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

async function fixStudentStatusConfirm() {
  if (!window._studentsToReactivate) {
    console.error('❌ No pending students. Run fixStudentStatus() first.');
    return;
  }

  const { schoolId, students } = window._studentsToReactivate;

  console.log(`\n🔄 Reactivating ${students.length} students...`);

  let count = 0;
  for (const student of students) {
    count++;
    if (count <= 10 || count % 10 === 0) {
      console.log(`  [${count}/${students.length}] Reactivating: ${student.firstName} ${student.surname}`);
    }
    const studentRef = doc(db, `schools/${schoolId}/students`, student.id);
    await updateDoc(studentRef, {
      status: 'active'
    });
  }

  console.log(`\n✅ Complete! ${students.length} students are now active.`);
  delete window._studentsToReactivate;

  return { success: true, reactivated: students.length };
}

export { fixStudentStatus, fixStudentStatusConfirm };